import { RuleGroupType, RuleType } from 'react-querybuilder';
import jsonLogic from 'json-logic-js';
import { Rule, JsonLogicRule } from '../types';

export const convertQueryToJsonLogic = (query: RuleGroupType): any => {
  if (!query.rules || query.rules.length === 0) {
    return null;
  }

  const combinator = query.combinator.toLowerCase();
  const rules = query.rules.map((rule) => {
    if ('rules' in rule) {
      return convertQueryToJsonLogic(rule as RuleGroupType);
    } else {
      const r = rule as RuleType;
      return convertRuleToJsonLogic(r);
    }
  }).filter(Boolean);

  if (rules.length === 0) return null;
  if (rules.length === 1) return rules[0];

  return { [combinator]: rules };
};

const convertRuleToJsonLogic = (rule: RuleType): any => {
  const { field, operator, value } = rule;

  switch (operator) {
    case '=':
      return { '==': [{ var: field }, value] };
    case '!=':
      return { '!=': [{ var: field }, value] };
    case '<':
      return { '<': [{ var: field }, value] };
    case '>':
      return { '>': [{ var: field }, value] };
    case '<=':
      return { '<=': [{ var: field }, value] };
    case '>=':
      return { '>=': [{ var: field }, value] };
    case 'contains':
      return { in: [value, { var: field }] };
    case 'beginsWith':
      return { '!!': [{ match: [{ var: field }, `^${value}`] }] };
    case 'endsWith':
      return { '!!': [{ match: [{ var: field }, `${value}$`] }] };
    case 'doesNotContain':
      return { '!': { in: [value, { var: field }] } };
    case 'doesNotBeginWith':
      return { '!': { '!!': [{ match: [{ var: field }, `^${value}`] }] } };
    case 'doesNotEndWith':
      return { '!': { '!!': [{ match: [{ var: field }, `${value}$`] }] } };
    case 'null':
      return { '==': [{ var: field }, null] };
    case 'notNull':
      return { '!=': [{ var: field }, null] };
    case 'in':
      return { in: [{ var: field }, value] };
    case 'notIn':
      return { '!': { in: [{ var: field }, value] } };
    case 'between':
      if (Array.isArray(value) && value.length === 2) {
        return {
          and: [
            { '>=': [{ var: field }, value[0]] },
            { '<=': [{ var: field }, value[1]] }
          ]
        };
      }
      return null;
    case 'notBetween':
      if (Array.isArray(value) && value.length === 2) {
        return {
          or: [
            { '<': [{ var: field }, value[0]] },
            { '>': [{ var: field }, value[1]] }
          ]
        };
      }
      return null;
    default:
      return null;
  }
};

export const convertJsonLogicToQuery = (logic: any): RuleGroupType => {
  if (!logic) {
    return {
      combinator: 'and',
      rules: []
    };
  }

  if ('and' in logic) {
    return {
      combinator: 'and',
      rules: logic.and.map(convertJsonLogicToQuery)
    };
  }

  if ('or' in logic) {
    return {
      combinator: 'or',
      rules: logic.or.map(convertJsonLogicToQuery)
    };
  }

  const rule = convertJsonLogicToRule(logic);
  if (rule) {
    return {
      combinator: 'and',
      rules: [rule]
    };
  }

  return {
    combinator: 'and',
    rules: []
  };
};

const convertJsonLogicToRule = (logic: any): RuleType | null => {
  if ('==' in logic && Array.isArray(logic['==']) && logic['=='].length === 2) {
    const [left, right] = logic['=='];
    if (left && typeof left === 'object' && 'var' in left) {
      return {
        field: left.var,
        operator: '=',
        value: right
      };
    }
  }

  if ('!=' in logic && Array.isArray(logic['!=']) && logic['!='].length === 2) {
    const [left, right] = logic['!='];
    if (left && typeof left === 'object' && 'var' in left) {
      return {
        field: left.var,
        operator: '!=',
        value: right
      };
    }
  }

  if ('<' in logic && Array.isArray(logic['<']) && logic['<'].length === 2) {
    const [left, right] = logic['<'];
    if (left && typeof left === 'object' && 'var' in left) {
      return {
        field: left.var,
        operator: '<',
        value: right
      };
    }
  }

  if ('>' in logic && Array.isArray(logic['>']) && logic['>'].length === 2) {
    const [left, right] = logic['>'];
    if (left && typeof left === 'object' && 'var' in left) {
      return {
        field: left.var,
        operator: '>',
        value: right
      };
    }
  }

  if ('<=' in logic && Array.isArray(logic['<=']) && logic['<='].length === 2) {
    const [left, right] = logic['<='];
    if (left && typeof left === 'object' && 'var' in left) {
      return {
        field: left.var,
        operator: '<=',
        value: right
      };
    }
  }

  if ('>=' in logic && Array.isArray(logic['>=']) && logic['>='].length === 2) {
    const [left, right] = logic['>='];
    if (left && typeof left === 'object' && 'var' in left) {
      return {
        field: left.var,
        operator: '>=',
        value: right
      };
    }
  }

  if ('in' in logic && Array.isArray(logic['in']) && logic['in'].length === 2) {
    const [left, right] = logic['in'];
    if (right && typeof right === 'object' && 'var' in right) {
      return {
        field: right.var,
        operator: 'contains',
        value: left
      };
    }
    if (left && typeof left === 'object' && 'var' in left) {
      return {
        field: left.var,
        operator: 'in',
        value: right
      };
    }
  }

  return null;
};

export const exportRuleToJson = (rule: Rule): JsonLogicRule => {
  return {
    filters: convertQueryToJsonLogic(rule.filters),
    triggers: convertQueryToJsonLogic(rule.triggers),
    notification: rule.notification,
    metadata: {
      id: rule.id,
      name: rule.name,
      description: rule.description,
      active: rule.active,
      createdAt: rule.createdAt.toISOString(),
      updatedAt: rule.updatedAt.toISOString()
    }
  };
};

export const importRuleFromJson = (jsonRule: JsonLogicRule): Rule => {
  return {
    id: jsonRule.metadata?.id || '',
    name: jsonRule.metadata?.name || 'Imported Rule',
    description: jsonRule.metadata?.description,
    filters: jsonRule.filters ? convertJsonLogicToQuery(jsonRule.filters) : { combinator: 'and', rules: [] },
    triggers: jsonRule.triggers ? convertJsonLogicToQuery(jsonRule.triggers) : { combinator: 'and', rules: [] },
    notification: jsonRule.notification,
    active: jsonRule.metadata?.active ?? true,
    createdAt: jsonRule.metadata?.createdAt ? new Date(jsonRule.metadata.createdAt) : new Date(),
    updatedAt: jsonRule.metadata?.updatedAt ? new Date(jsonRule.metadata.updatedAt) : new Date()
  };
};

export const evaluateRule = (rule: Rule, data: any): boolean => {
  const filterLogic = convertQueryToJsonLogic(rule.filters);
  const triggerLogic = convertQueryToJsonLogic(rule.triggers);

  const filterPassed = !filterLogic || jsonLogic.apply(filterLogic, data);
  const triggerPassed = !triggerLogic || jsonLogic.apply(triggerLogic, data);

  return filterPassed && triggerPassed;
};